import { Box, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DataTable from 'components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useGetBusinesses } from 'apis/agency/hook';
import { ReactNode, useState } from 'react';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { formatRevenue } from 'utils/helperFunctions';
import Select from 'components/Common/Select';
import { useForm } from 'react-hook-form';
import { BusinessProfileResponse } from 'apis/business/types';
import dayjs from 'dayjs';
import { useGetAgencyRevenueAnalytics, useGetAgencyRevenueBreakdown } from '@/apis/agency/revenue/hook';
import { RevenueBreakdown } from '@/apis/agency/revenue/types';
import LocationIcon from '@/assets/icons/LocationIcon';
import RevenueLoader from './components/Loader';
import { CardsBusinessIcon } from '@/assets/icons/CardsBusinessIcon';
import CardDollarIcon from '@/assets/icons/CardDollarIcon';
import MonthlyRevenueChart from './components/MonthlyRevenueChart';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import RevenueDetailsDrawer from './components/RevenueDetailsDrawer';
import AgencyRevenueIcon from '@/assets/icons/AgencyRevenueIcon';

export const getStatus = (status: string, theme: any) => {
  switch (status) {
    case 'pending':
    case 'draft':
      return { color: '#777777', bgcolor: '#ECECEC', label: 'Draft' };

    case 'finalized':
    case 'failed':
      return { color: '#7C00B1', bgcolor: '#F5DFFF', label: 'Open' };

    case 'succeeded':
      return { color: '#106520', bgcolor: '#BAE7CC', label: 'Paid' };

    case 'voided':
      return { color: '#FB8703', bgcolor: '#FFF3E5', label: 'Voided' };

    default:
      return {
        color: theme.palette.primary.dark,
        bgcolor: theme.palette.primary.light,
        label: typeof status === 'string' ? status.charAt(0).toUpperCase() + status.slice(1) : status
      };
  }
};

export default function Revenue() {
  const theme = useTheme();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();
  const [selectedRevenue, setSelectedRevenue] = useState<RevenueBreakdown | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data: businesses, isLoading } = useGetBusinesses({ per_page: 9999, filter: { for_revenue: true } });

  const { control, watch } = useForm({
    defaultValues: {
      business_id: ' '
    }
  });
  const filters: Record<string, any> = watch();
  const updatedFilters = {
    filter: {
      business_id: filters?.business_id
    }
  };

  const { data: revenueBreakDownData, isLoading: isBreakdownLoading } = useGetAgencyRevenueBreakdown({
    ...updatedFilters,
    page: currentPage,
    per_page: perPage
  });
  const { data: revenueAnalyticsData, isLoading: isAnalyticsLoading } = useGetAgencyRevenueAnalytics(updatedFilters);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', pb: { xs: 28, sm: 0 } }}>
      {/* Header with Filters */}
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        gap={{ xs: 2, lg: 0 }}
        marginBottom={3}
      >
        <Box>
          <Typography variant='h6' fontWeight={500}>
            Revenue
          </Typography>
          <Typography variant='body2' color='#777' mt={0.5}>
            Analyze your agency revenue across all businesses.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          pt={{ xs: 0, md: 0.6 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          width={{ xs: '100%', lg: 'auto' }}
          flexWrap='wrap'
        >
          <Box minWidth={{ xs: '100%', sm: 250 }} width={{ xs: '100%', sm: 'auto' }}>
            <Select
              name='business_id'
              label=''
              control={control}
              searchable
              disabled={isLoading}
              sortOrder='none'
              options={[
                { value: ' ', label: 'All Business' },
                ...(businesses?.data.map((business: BusinessProfileResponse) => ({
                  value: business.id,
                  label: business.name
                })) ?? [])
              ]}
              sx={{ height: '39px', minWidth: { xs: '100%', sm: 250 }, background: theme.palette.background.paper }}
            />
          </Box>
        </Stack>
      </Box>
      {(isBreakdownLoading || isAnalyticsLoading) && <RevenueLoader />}
      {/* Analytics Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <AnalyticsCard
          title='All Time Revenue'
          value={formatRevenue(revenueAnalyticsData?.all_time_revenue as number)}
          icon={<CardDollarIcon />}
        />
        <AnalyticsCard
          title='Avg. Monthly Revenue'
          value={formatRevenue(revenueAnalyticsData?.avg_monthly_revenue as number)}
          subtitle='/Business'
          icon={<CardDollarIcon />}
        />
        <AnalyticsCard
          title='Active Businesses'
          value={revenueAnalyticsData?.active_businesses}
          subtitle={
            revenueAnalyticsData?.inactive_businesses != null
              ? `· ${revenueAnalyticsData.inactive_businesses} Inactive`
              : undefined
          }
          icon={<CardsBusinessIcon />}
        />
        <AnalyticsCard
          title='Active Locations'
          value={revenueAnalyticsData?.active_locations}
          subtitle={
            revenueAnalyticsData?.inactive_locations != null
              ? `· ${revenueAnalyticsData.inactive_locations} Inactive`
              : undefined
          }
          icon={<LocationIcon size={18} />}
        />
      </Grid>
      <MonthlyRevenueChart data={revenueAnalyticsData?.monthly_revenue_trend || []} />
      {/* Breakdown Table */}
      <Typography variant='h2' fontWeight={500} mt={3} mb={0.5} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
        Business Revenue Breakdown
      </Typography>
      <Typography variant='body2' mb={2} mt={0.5} color='#777' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
        Revenue from each business client
      </Typography>
      <DataTable
        data={revenueBreakDownData?.data || []}
        columns={columns(theme, (revenue) => {
          setSelectedRevenue(revenue);
          setIsDrawerOpen(true);
        })}
        isHeaderSticky={true}
        isLoading={isLoading}
      />
      <Pagination
        totalPages={revenueBreakDownData?.meta?.total_pages}
        totalCount={revenueBreakDownData?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
      <RevenueDetailsDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} data={selectedRevenue} />
    </Box>
  );
}

const columns = (theme: any, onOpenDetails: (data: RevenueBreakdown) => void): ColumnDef<RevenueBreakdown>[] => [
  {
    header: 'Business Name',
    accessorKey: 'business_name',
    size: 200,
    cell: ({ row }) => {
      const { business_name, payment_method_status } = row.original;
      let icon = null;

      if (payment_method_status === 'attached') {
        icon = <AgencyRevenueIcon color={theme.palette.primary.main} />;
      } else if (payment_method_status === 'expired') {
        icon = <AgencyRevenueIcon color={theme.palette.error.main} />;
      }
      return (
        <Box display='flex' alignItems='center' gap={1}>
          <Typography noWrap>{business_name}</Typography>
          {icon && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      );
    }
  },
  {
    header: 'Package',
    accessorKey: 'package',
    cell: ({ getValue }: any) =>
      getValue() && (
        <Box
          component='span'
          sx={{
            px: { xs: 1, sm: 1.5 },
            py: 0.5,
            borderRadius: '12px',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: 500,
            color: theme.palette.primary.main,
            bgcolor: theme.palette.primary.light
          }}
        >
          {getValue()}
        </Box>
      )
  },
  {
    header: 'All Time Revenue',
    accessorKey: 'all_time_revenue',
    cell: ({ getValue }) => (
      <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{formatRevenue(getValue<number>())}</Typography>
    )
  },
  {
    header: 'Last Month Revenue',
    accessorKey: 'last_month_revenue',
    cell: ({ getValue }) => (
      <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{formatRevenue(getValue<number>())}</Typography>
    )
  },
  {
    header: 'Locations',
    accessorKey: 'locations',
    size: 80,
    cell: ({ getValue }) => (
      <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{getValue() as number}</Typography>
    )
  },
  {
    header: 'Last Payment',
    accessorKey: 'last_month_payment_date',
    cell: ({ getValue }) => (
      <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
        {dayjs(getValue<string>()).format('MMM DD, YYYY')}
      </Typography>
    )
  },
  {
    header: 'STATUS',
    accessorKey: 'status',
    size: 120,
    cell: ({ row }) =>
      row.original.status ? (
        <Chip
          label={getStatus(row.original.status, theme).label}
          size='small'
          sx={{
            color: getStatus(row.original.status, theme).color,
            bgcolor: getStatus(row.original.status, theme).bgcolor,
            borderRadius: 6,
            px: 1.5,
            fontSize: '12px',
            fontWeight: 500,
            height: 28
          }}
        />
      ) : (
        ''
      )
  },
  {
    header: 'Action',
    size: 80,
    cell: ({ row }) => (
      <Box display='flex' gap={0.5} alignItems='center'>
        <Box fontSize={18} borderRadius='50%' sx={{ bgcolor: theme.palette.primary.light }}>
          <IconButton onClick={() => onOpenDetails(row.original as RevenueBreakdown)}>
            <MdOutlineRemoveRedEye color={theme.palette.primary.main} size={18} />
          </IconButton>
        </Box>
      </Box>
    )
  }
];

const AnalyticsCard = ({
  title,
  value,
  subtitle,
  icon
}: {
  title: string;
  value?: string | number;
  subtitle?: string;
  icon: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Grid
      size={{ xs: 12, sm: 6, md: 3 }}
      bgcolor='#fff'
      border='1px solid #ECECEC'
      paddingY={1.5}
      paddingX={2}
      borderRadius={2}
      borderLeft={`3px solid ${theme.palette.primary.main}`}
    >
      <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
        <Typography color='secondary' fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {title}
        </Typography>
        <Box
          bgcolor={theme.palette.primary.light}
          color={theme.palette.primary.main}
          borderRadius='50%'
          height={{ xs: '28px', sm: '32px' }}
          width={{ xs: '28px', sm: '32px' }}
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          {icon}
        </Box>
      </Box>
      <Stack direction='row' alignItems='baseline' spacing={1}>
        <Typography variant='h5' mt={1} fontWeight={500} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {value ?? 0}
        </Typography>
        {subtitle && (
          <Typography fontSize={12} color='#777'>
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Grid>
  );
};
